import { useEffect, useMemo, useState } from "react";
import {
  RefreshCw,
  Mail,
  Download,
  Search,
  Trash2,
  Loader2,
} from "lucide-react";
import {
  fetchSubscribers,
  deleteSubscriber,
  buildNewsletterCsvExportUrl,
} from "@/admin/newsletterAdminApi";

/**
 * Admin newsletter list at /admin/newsletter.
 *
 * Surface:
 *   - search field (case-insensitive substring match on email)
 *   - "Refresh" + "Export CSV" actions in the header
 *   - table-like list of {email, source, language, date} rows with a
 *     trash button to remove a subscriber (uses a confirm() prompt — a
 *     destructive action with no undo, so explicit confirmation is fine)
 *   - pagination via "load more" (cheap and unambiguous)
 *
 * Mirrors the visual pattern used by `AdminRequestsList` so the admin
 * panel feels consistent.
 */
export default function AdminNewsletterList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState("");

  const PAGE = 100;

  // Debounce the search box so we don't slam the backend on every key.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(handle);
  }, [q]);

  const load = async ({ append = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError("");
    try {
      const next = await fetchSubscribers({
        q: debouncedQ,
        limit: PAGE,
        skip: append ? items.length : 0,
      });
      if (append) {
        setItems((prev) => [...prev, ...(next.items || [])]);
      } else {
        setItems(next.items || []);
        setSkip(0);
      }
      setTotal(next.total || 0);
    } catch (e) {
      setError(e?.message || "Failed to load subscribers.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Reload whenever the debounced search query changes.
  useEffect(() => {
    load({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const handleRemove = async (sub) => {
    const ok = window.confirm(
      `Remove ${sub.email} from the newsletter list? This cannot be undone.`
    );
    if (!ok) return;
    setRemovingId(sub.id);
    try {
      await deleteSubscriber(sub.id);
      setItems((prev) => prev.filter((s) => s.id !== sub.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      setError(e?.message || "Failed to remove subscriber.");
    } finally {
      setRemovingId("");
    }
  };

  const csvHref = useMemo(
    () => buildNewsletterCsvExportUrl({ q: debouncedQ }),
    [debouncedQ]
  );

  const canLoadMore = items.length < total;

  return (
    <section
      className="max-w-5xl"
      data-testid="admin-newsletter-page"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="sas-eyebrow mb-2">Newsletter</div>
          <h1 className="sas-h1" data-testid="admin-newsletter-heading">
            Newsletter subscribers
          </h1>
          <p className="sas-body mt-3 max-w-2xl text-[15px]">
            Email addresses captured from the public newsletter form. No
            outbound email pipeline is wired up yet — these rows are stored
            for when one is.
          </p>
        </div>

        <div
          className="flex items-center gap-2 flex-wrap"
          data-testid="admin-newsletter-actions"
        >
          <button
            type="button"
            onClick={() => load({ append: false })}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
            data-testid="admin-newsletter-refresh"
          >
            <RefreshCw size={14} aria-hidden />
            Refresh
          </button>
          <a
            href={csvHref}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
            data-testid="admin-newsletter-export-csv"
            title="Export current filter as CSV"
            download
          >
            <Download size={14} aria-hidden />
            Export CSV
          </a>
        </div>
      </div>

      {/* Search + count */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search
            size={14}
            aria-hidden
            className="absolute top-1/2 -translate-y-1/2 start-3 text-[color:var(--sas-ink-2)]"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email…"
            className="sas-input ps-9"
            data-testid="admin-newsletter-search"
          />
        </div>
        <div
          className="text-[12px] text-[color:var(--sas-ink-2)]"
          data-testid="admin-newsletter-total"
        >
          {total === 1 ? "1 subscriber" : `${total} subscribers`}
        </div>
      </div>

      {error && (
        <div
          className="mt-4 rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5 text-[13px] text-[#991b1b]"
          role="alert"
          data-testid="admin-newsletter-error"
        >
          {error}
        </div>
      )}

      {/* List */}
      <div
        className="mt-5 rounded-xl border border-[color:var(--sas-line)] bg-white overflow-hidden"
        data-testid="admin-newsletter-list"
      >
        {/* Header row (sm+) */}
        <div className="hidden sm:grid grid-cols-[1.6fr_0.7fr_0.5fr_0.9fr_auto] items-center gap-3 px-4 py-2.5 text-[11px] uppercase tracking-wide text-[color:var(--sas-ink-2)] border-b border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/30">
          <div>Email</div>
          <div>Source</div>
          <div>Lang</div>
          <div>Subscribed at</div>
          <div className="text-end">Actions</div>
        </div>

        {loading && items.length === 0 ? (
          <div
            className="px-4 py-12 text-center text-[13px] text-[color:var(--sas-ink-2)] flex items-center justify-center gap-2"
            data-testid="admin-newsletter-loading"
          >
            <Loader2 size={14} className="animate-spin" aria-hidden />
            Loading subscribers…
          </div>
        ) : items.length === 0 ? (
          <div
            className="px-6 py-14 text-center"
            data-testid="admin-newsletter-empty"
          >
            <div
              className="mx-auto w-12 h-12 rounded-full flex items-center justify-center"
              style={{
                background: "var(--sas-accent-soft)",
                color: "var(--sas-accent)",
              }}
              aria-hidden
            >
              <Mail size={20} />
            </div>
            <p className="mt-4 text-[14px] text-[color:var(--sas-ink-2)]">
              {debouncedQ
                ? `No subscribers match "${debouncedQ}".`
                : "No subscribers yet."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--sas-line)]">
            {items.map((sub) => (
              <li
                key={sub.id}
                className="grid grid-cols-1 sm:grid-cols-[1.6fr_0.7fr_0.5fr_0.9fr_auto] items-center gap-2 sm:gap-3 px-4 py-3 text-[13.5px]"
                data-testid={`admin-newsletter-row-${sub.id}`}
              >
                <div className="font-medium text-[color:var(--sas-ink)] break-all" dir="ltr">
                  {sub.email}
                </div>
                <div className="text-[color:var(--sas-ink-2)]">
                  <span className="sas-chip">{sub.source}</span>
                </div>
                <div className="text-[color:var(--sas-ink-2)] uppercase text-[12px]">
                  {sub.language || "—"}
                </div>
                <div
                  className="text-[12.5px] text-[color:var(--sas-ink-2)]"
                  dir="ltr"
                >
                  {formatDate(sub.created_at)}
                </div>
                <div className="flex sm:justify-end">
                  <button
                    type="button"
                    onClick={() => handleRemove(sub)}
                    disabled={removingId === sub.id}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] text-[color:var(--sas-ink-2)] hover:text-[#b91c1c] hover:bg-[#fef2f2] transition-colors"
                    aria-label={`Remove ${sub.email}`}
                    data-testid={`admin-newsletter-remove-${sub.id}`}
                  >
                    {removingId === sub.id ? (
                      <Loader2 size={13} className="animate-spin" aria-hidden />
                    ) : (
                      <Trash2 size={13} aria-hidden />
                    )}
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {canLoadMore && (
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={() => load({ append: true })}
            disabled={loadingMore}
            className="sas-btn sas-btn-ghost"
            data-testid="admin-newsletter-load-more"
          >
            {loadingMore ? (
              <>
                <Loader2 size={14} className="animate-spin" aria-hidden />
                Loading…
              </>
            ) : (
              `Load more (${total - items.length} remaining)`
            )}
          </button>
        </div>
      )}
    </section>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
